import { HttpParams } from '@angular/common/http'
import { Component, ElementRef } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Globalize, Tooltip } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellType, DataMap, GridPanel } from '@grapecity/wijmo.grid'
import { HrGoalComponent } from 'src/app/hr/scorecard/modals/hr-goal.component'
import { getInitials } from 'src/app/shared/shared-functions'

import { BaseComponent, FeatureType, Grid, HrGoal, ListItem, ModalSize, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    templateUrl: './metrics.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class MetricsComponent extends BaseComponent {
    faSearch = faSearch
    public grid: Grid<HrGoal>

    allUsers: VpUser[] = []
    allUserMap!: DataMap

    weekCols = [] as string[]
    isCalculating = false
    private tooltip = new Tooltip()

    protected goalDirectionMap = new DataMap([new ListItem(0, '>='), new ListItem(1, '<='), new ListItem(2, '>= <=')], 'value', 'display')

    constructor(private el: ElementRef) {
        super()
        this.grid = new Grid<HrGoal>(this.services)
            .setupModalEditing(this.services.data.hrGoals, 'Goal', HrGoalComponent, () => new HrGoal(), this, ModalSize.medium)
            .filterBy(item => [item.name])
            .wrapColumnHeaders()

        this.refresh()
    }

    onGridInitialized() {
        // Set up click handler for meetings column after grid is initialized
        const flexGrid = this.grid.flexGrid
        if (flexGrid) {
            const hostElement = flexGrid.hostElement
            const onClick = (e: MouseEvent) => {
                const hitTest = flexGrid.hitTest(e)
                const col = hitTest.getColumn()
                if (col && col.binding === 'meetingNames') {
                    const row = hitTest.getRow()
                    const dataItem = row?.dataItem as HrGoal
                    if (dataItem && dataItem.meetingNames && dataItem.meetingNames.length > 0) {
                        const cellBounds = flexGrid.getCellBoundingRect(hitTest.row, hitTest.col)
                        const meetingNamesText = dataItem.meetingNames.join('<br>')
                        this.tooltip.show(hostElement, meetingNamesText, cellBounds)
                    }
                }
            }
            hostElement.addEventListener('click', onClick, false)
        }
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.hrGoals.queryAll(),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]

                        this.allUsers = result[1]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'name')

                        this.generateColumns()
                    },
                }),
        )
    }

    generateColumns() {
        this.weekCols = []
        setTimeout(() => {
            const cols = [] as string[]

            let indexOfMostWeekValues = 0
            let weekValuesLength = 0
            for (let i = 0; i < this.grid.items.length; i++) {
                const currentItemLength = this.grid.items[0].weekValues.length
                if (currentItemLength > weekValuesLength) {
                    indexOfMostWeekValues = i
                    weekValuesLength = currentItemLength
                }
            }

            const weeks = this.grid.items[indexOfMostWeekValues].weekValues
            for (const prop in weeks) {
                cols.push(prop)
            }
            this.weekCols = cols
        }, 1)
    }

    goalsGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.rows[r]
            const item = row.dataItem as HrGoal
            cell.classList.remove('red-cell')
            cell.classList.remove('green-cell')

            if (panel.grid.activeEditor) console.log(panel.grid.activeEditor)

            if (col.binding === 'goal1') {
                if (item.goalDirection < 2) {
                    cell.innerHTML = Globalize.format(item.goal1, item.format)
                } else {
                    cell.innerHTML = Globalize.format(item.goal1, item.format) + ' - ' + Globalize.format(item.goal2, item.format)
                }
            }
            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = getInitials(originalVal)
            }

            if (col.binding === 'meetingNames') {
                const meetingCount = item.meetingNames?.length || 0
                if (meetingCount > 0) {
                    cell.innerHTML = `${meetingCount} Meeting${meetingCount !== 1 ? 's' : ''}`
                    cell.style.cursor = 'pointer'
                    cell.style.textDecoration = 'underline'
                    cell.style.color = '#007bff'
                } else {
                    cell.innerHTML = ''
                }
            }

            if (col.binding === 'average') {
                cell.innerHTML = Globalize.format(item.average, item.format)
                if (!item.showAverage) cell.innerHTML = ''
            }

            if (col.binding === 'sum') {
                cell.innerHTML = Globalize.format(item.sum, item.format)

                // Don't show the sum for percents, since it doesn't really make sense.
                if (item.format[0].toUpperCase() === 'P' || !item.showSum) cell.innerHTML = ''
            }

            if (c > 8) {
                if (!panel.grid.activeEditor) {
                    // apply the format
                    const originalVal = item.weekValues[col.binding!.split('.')[1]]
                    cell.innerHTML = Globalize.format(originalVal, item.format)

                    // apply the goal meeting color
                    if (originalVal != null) {
                        switch (item.goalDirection) {
                            case 0:
                                if (originalVal >= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 1:
                                if (originalVal <= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 2:
                                if (originalVal >= item.goal1 && originalVal <= item.goal2) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            default:
                        }
                    }
                }
            }
        }
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    public calculateMetrics() {
        if (this.isCalculating) return

        this.isCalculating = true
        this.cancelableRequest(
            'CalculateMetrics',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/HrGoal/CalculateMetrics')).subscribe({
                next: () => {
                    this.services.notification.success('Metrics calculated successfully')
                    this.refresh()
                },
                error: () => {
                    this.services.notification.error('Failed to calculate metrics')
                },
                complete: () => {
                    this.isCalculating = false
                },
            }),
        )
    }
}
